import { DatadogService } from "@core/services/datadog.service";
import {
	HttpContext,
	HttpContextToken,
	HttpErrorResponse,
	HttpEvent,
	HttpHandler,
	HttpInterceptor,
	HttpRequest
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { AuthService } from "@core/services/auth/auth.service";
import { AlertsService } from "@shared/components/atlas/alerts/services";
import { GLOBAL_ALERT_KEY } from "@shared/components/atlas/alerts/types";
import { BffErrorResponse } from "@shared/http-responses/bff-error-response";
import { HTTP_STATUS_CODE } from "@shared/utils/http-status-code";
import { NgxRolesService } from "ngx-permissions";
import { BehaviorSubject, Observable, throwError } from "rxjs";
import { catchError, filter, switchMap, take } from "rxjs/operators";

const IGNORE_HTTP_ERROR = new HttpContextToken<boolean>(() => false);

export function ignoreHttpError(): HttpContext {
	return new HttpContext().set(IGNORE_HTTP_ERROR, true);
}

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
	private isRefreshing = false;
	private _refreshTokenSubject = new BehaviorSubject<unknown>(null);

	constructor(
		private alertsService: AlertsService,
		private authService: AuthService,
		private roles: NgxRolesService,
		private datadogService: DatadogService
	) {}

	intercept(
		req: HttpRequest<unknown>,
		next: HttpHandler
	): Observable<HttpEvent<unknown>> {
		if (req.context.get(IGNORE_HTTP_ERROR)) {
			return next.handle(req);
		}

		return next.handle(req).pipe(
			catchError(({ status, error, message }: BffErrorResponse) => {
				switch (status) {
					case HTTP_STATUS_CODE.UNAUTHORIZED:
						if (!req.url.includes("/auth/refresh-token")) {
							return this.handle401Error(req, next);
						}

						this.datadogService.customLog(
							"info",
							"(Deslogar usuário) - Usuário deslogado por falha no refresh-token (401)",
							{
								date: new Date().toLocaleString("pt-BR"),
								user: this.authService.getUserLogin()
							}
						);

						this.authService.logout().subscribe();

						break;

					case HTTP_STATUS_CODE.FORBIDDEN:
						this.alertsService.error({
							key: GLOBAL_ALERT_KEY,
							message: error.message.description,
							title: error.message.title
						});

						this.datadogService.customLog(
							"info",
							"(Acesso negado) - Usuário com acesso negado (403)",
							{
								url: req.url,
								date: new Date().toLocaleString("pt-BR"),
								user: this.authService.getUserLogin()
							}
						);

						break;

					case HTTP_STATUS_CODE.NOT_FOUND:
						this.alertsService.error({
							key: GLOBAL_ALERT_KEY,
							message: error.message.description,
							title: error.message.title
						});

						break;

					case HTTP_STATUS_CODE.INTERNAL_SERVER_ERROR:
						this.alertsService.error({
							key: GLOBAL_ALERT_KEY,
							message: error.message.description,
							title: error.message.title
						});
						break;

					case HTTP_STATUS_CODE.BAD_REQUEST:
						this.alertsService.error({
							key: GLOBAL_ALERT_KEY,
							message: error.message.description ?? error.message,
							title: error.message.title
						});
						break;

					default:
						this.alertsService.error({
							key: GLOBAL_ALERT_KEY,
							message: "Ocorreu um erro!"
						});
						break;
				}
				return throwError(() => message);
			})
		);
	}

	private handle401Error(req: HttpRequest<unknown>, next: HttpHandler) {
		if (!this.isRefreshing) {
			this.isRefreshing = true;
			this._refreshTokenSubject.next(null);
			if (this.authService.getRefreshToken()) {
				return this.authService.refreshToken().pipe(
					switchMap((response) => {
						this.isRefreshing = false;
						this._refreshTokenSubject.next(response.access_token);
						return next.handle(
							req.clone({
								headers: req.headers.set(
									"Authorization",
									"Bearer " + (response.access_token ?? "")
								)
							})
						);
					}),
					catchError((err: HttpErrorResponse) => {
						this.isRefreshing = false;
						this.authService.logout().subscribe();
						return throwError(() => err);
					})
				);
			}
		}

		return this._refreshTokenSubject.pipe(
			filter((token) => token != null),
			take(1),
			switchMap((jwt) => {
				return next.handle(
					req.clone({
						headers: req.headers.set(
							"Authorization",
							"Bearer " + String(jwt)
						)
					})
				);
			})
		);
	}
}
